import { Directive, OnInit, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GungNotificationService } from 'gung-common';
import { ListItemRendererComponent } from 'gung-list';
import { first } from 'rxjs';
import { PunchoutAuditListItem, PunchoutAuditLogEntry } from '../../../models/punchout';
import { FeatureActivatedGuardWrapper } from '../../../guards/feature-activated/feature-activated.guard';
import { GungModalService } from '../../../services/gung-modal/gung-modal.service';
import { PunchoutAuditAdminService } from '../../../services/punchout/punchout-audit-admin.service';

/**
 * Shared behaviour for the punchout audit tables.
 *
 * The list endpoint returns every entry fully hydrated, so list rows strip the heavy fields on
 * arrival and deliberately carry no payloads. Inspecting a row re-fetches the full entry by id
 * before opening the detail modal.
 */
@Directive()
export abstract class PunchoutAuditTableBase
  extends ListItemRendererComponent<PunchoutAuditListItem[]>
  implements OnInit
{
  protected auditService = inject(PunchoutAuditAdminService);
  protected modalService = inject(GungModalService);
  protected notificationService = inject(GungNotificationService);
  protected translateService = inject(TranslateService);
  protected featureGuard = inject(FeatureActivatedGuardWrapper);

  /**
   * Whether the backend can process punchout order intake at all. Receiving order requests does
   * not need the EDI module, but processing them does. Without it, the reprocess endpoint fails
   * and the EDI order route does not exist.
   */
  ediEnabled = false;

  /** Id of the row currently being fetched, so only that row shows a spinner. */
  loadingId?: string;

  ngOnInit(): void {
    this.featureGuard
      .isActivated('gung-feature-edi', '1.0.0', true)
      .pipe(first())
      .subscribe(activated => (this.ediEnabled = activated));
  }

  inspect(item: PunchoutAuditListItem): void {
    if (this.loadingId) {
      return;
    }
    this.loadingId = item.id;
    this.auditService
      .get(item.id)
      .pipe(first())
      .subscribe({
        next: entry => {
          this.loadingId = undefined;
          this.openDetail(item, entry);
        },
        error: () => {
          this.loadingId = undefined;
          this.notificationService.notify('', this.translateService.instant('ERROR'), 'error');
        }
      });
  }

  private openDetail(item: PunchoutAuditListItem, entry: PunchoutAuditLogEntry): void {
    this.modalService
      .openPunchoutAuditDetail(entry, { configLabel: item.configLabel, ediEnabled: this.ediEnabled })
      .then(updated => {
        if (updated) {
          this.applyUpdate(item, updated);
        }
      })
      .catch(() => undefined);
  }

  /**
   * Patches the row in place after a reprocess, instead of re-fetching the whole list. The row
   * object is the same reference filter-list holds, so mutating it updates the rendered table.
   */
  protected applyUpdate(item: PunchoutAuditListItem, updated: PunchoutAuditLogEntry): void {
    item.status = updated.status;
    item.ediMessageId = updated.ediMessageId;
    item.errorMessage = updated.errorMessage;
  }
}
